import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, X, Share } from 'lucide-react'
import {
  useInstallPromptStore,
  checkDismissalCooldown,
  type BeforeInstallPromptEvent,
} from '@/stores/install-prompt'

/** Detect iOS Safari (no beforeinstallprompt support) */
function isIosSafari(): boolean {
  const ua = navigator.userAgent
  return /iP(hone|od|ad)/.test(ua) && /WebKit/.test(ua) && !/(CriOS|FxiOS|OPiOS|mercury)/.test(ua)
}

/** Check if the app is already installed / running in standalone mode */
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as Record<string, unknown>).standalone === true
  )
}

export function InstallBanner() {
  const { t } = useTranslation()
  const { deferredPrompt, dismissed, setDeferredPrompt, dismiss } = useInstallPromptStore()
  const [showIosGuide, setShowIosGuide] = useState(false)

  // Listen for the beforeinstallprompt event (Chromium browsers)
  useEffect(() => {
    checkDismissalCooldown()

    if (isStandalone()) return

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [setDeferredPrompt])

  // Determine if we should show the banner
  const canShow = !dismissed && !isStandalone() && (!!deferredPrompt || isIosSafari())

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === 'accepted') {
        dismiss()
      }
      setDeferredPrompt(null)
    } else if (isIosSafari()) {
      setShowIosGuide((v) => !v)
    }
  }, [deferredPrompt, dismiss, setDeferredPrompt])

  if (!canShow) return null

  return (
    <div className="fixed right-0 bottom-20 left-0 z-30 px-3 pb-2">
      <div className="glass-card border-primary/20 mx-auto max-w-lg rounded-2xl border p-3 shadow-lg">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="bg-primary/20 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
            <Download className="text-primary h-5 w-5" />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <p className="text-foreground text-sm font-semibold">{t('install.title')}</p>
            <p className="text-muted-foreground mt-0.5 text-xs leading-snug">
              {t('install.description')}
            </p>

            {/* iOS share instructions (toggle) */}
            {showIosGuide && (
              <div className="bg-surface-container-high mt-2 flex items-center gap-2 rounded-lg p-2 text-xs">
                <Share className="text-primary h-4 w-4 shrink-0" />
                <span className="text-muted-foreground">{t('install.iosGuide')}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={handleInstall}
                className="bg-primary text-primary-foreground rounded-lg px-4 py-1.5 text-xs font-semibold transition-opacity active:opacity-80"
              >
                {isIosSafari() ? t('install.howTo') : t('install.addButton')}
              </button>
              <button
                onClick={dismiss}
                className="text-muted-foreground text-xs transition-opacity active:opacity-60"
              >
                {t('install.notNow')}
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={dismiss}
            className="text-muted-foreground/60 shrink-0 p-1 transition-opacity active:opacity-60"
            aria-label={t('install.dismiss')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
